import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cn } from "../../utils/cn";
import {
  CONTROL_BASE,
  CONTROL_VARIANT_CLASSES,
  type ControlVariant,
} from "../controlStyles";

export type ButtonVariant = ControlVariant;
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Icon node rendered before the label (e.g. a Phosphor glyph). Sized via CSS. */
  iconLeft?: ReactNode;
  /** Icon node rendered after the label. */
  iconRight?: ReactNode;
}

// Heights map to the prototype (28/36/44); font reconciled to tokens (lg 15→h5).
// Corners are square (rounded-none) — the app-wide button style.
const sizes: Record<ButtonSize, string> = {
  sm: "h-7 gap-1.5 rounded-none px-3 text-body-sm [&_svg]:size-4",
  md: "h-9 gap-2 rounded-none px-4 text-body [&_svg]:size-[18px]",
  lg: "h-11 gap-2 rounded-none px-5 text-h5 [&_svg]:size-5",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = "primary",
      size = "md",
      iconLeft,
      iconRight,
      className,
      children,
      type = "button",
      ...rest
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type}
        className={cn(
          CONTROL_BASE,
          CONTROL_VARIANT_CLASSES[variant],
          sizes[size],
          className,
        )}
        {...rest}
      >
        {iconLeft}
        {children}
        {iconRight}
      </button>
    );
  },
);
